import { SHADOW_HOST_TAG } from '../constants';

/**
 * Creates a custom element with an attached Shadow DOM and returns the shadow root.
 * Re-uses an existing host element if one already lives in the document.
 */
export function getOrCreateShadowHost(): { host: HTMLElement; shadow: ShadowRoot } {
  let host = document.querySelector<HTMLElement>(SHADOW_HOST_TAG);
  if (host && host.shadowRoot) {
    return { host, shadow: host.shadowRoot };
  }

  host = document.createElement(SHADOW_HOST_TAG);
  host.style.all = 'initial'; // reset inherited styles

  const shadow = host.attachShadow({ mode: 'open' });
  document.body.appendChild(host);
  return { host, shadow };
}

/** Quick helper to create an element with optional className and textContent. */
export function el(
  tag: string,
  attrs?: Record<string, string>,
  ...children: (HTMLElement | string)[]
): HTMLElement {
  const elem = document.createElement(tag);
  if (attrs) {
    for (const [key, value] of Object.entries(attrs)) {
      if (key === 'className') {
        elem.className = value;
      } else if (key === 'textContent') {
        elem.textContent = value;
      } else {
        elem.setAttribute(key, value);
      }
    }
  }
  for (const child of children) {
    if (typeof child === 'string') {
      elem.appendChild(document.createTextNode(child));
    } else {
      elem.appendChild(child);
    }
  }
  return elem;
}

/** Remove all children from a node. */
export function clearChildren(node: Node): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}

/** Utility to apply inline event listener. */
export function on<K extends keyof HTMLElementEventMap>(
  element: HTMLElement,
  event: K,
  handler: (e: HTMLElementEventMap[K]) => void,
): void {
  element.addEventListener(event, handler);
}
